import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import P11Layout from "./P11Layout";
import { P11, PRIVACY_URL } from "./p11shared";
import { useP11Lang } from "./p11lang";

// Deelnemersprofiel: gegevens, identiteit, materiaal, privacy, account.
export default function P11Profile() {
  const nav = useNavigate();
  const { lang, tt } = useP11Lang();
  const [p, setP] = useState(null);
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/project11/me/profile").then(({ data }) => {
    setP(data); setPhone(data.phone || ""); setNotes(data.extra_notes || "");
  }).catch(() => nav("/project11/login"));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/project11/me/profile", { phone, extra_notes: notes, language: lang });
      toast.success(tt("save"));
      load();
    } catch { toast.error("Mislukt"); } finally { setBusy(false); }
  };

  if (!p) return <div className="p11-root min-h-screen flex items-center justify-center"><span className="p11-label">…</span></div>;
  const consentLabel = { terms: tt("c_terms"), privacy: tt("c_privacy"), health_truth: tt("c_health"), media: tt("c_media"), marketing: tt("c_marketing") };

  return (
    <P11Layout>
      <div data-testid="p11-profile">
        <div className="p11-label-yellow">{tt("my_profile")}</div>
        <h1 className="p11-h1 text-3xl mt-4 mb-10">{p.name}.</h1>

        <section className="mb-10" data-testid="p11-profile-identity">
          <div className="p11-label mb-4">// {tt("identity_system")}</div>
          <div className="border p-6 flex items-center justify-between" style={{ borderColor: P11.border }}>
            <div>
              <div className="p11-label">{tt("screen_name")}</div>
              <div className="p11-h2 text-xl mt-1">{(p.first_name || "").toUpperCase()}</div>
            </div>
            <div className="p11-mono text-3xl" style={{ color: P11.yellow }} data-testid="p11-profile-number">{p.number_display || "—"}</div>
          </div>
          <p className="p11-label mt-3">{lang === "nl" ? "Je nummer kan alleen door de organisatie worden aangepast." : "Your number can only be changed by the organization."}</p>
        </section>

        <section className="mb-10" data-testid="p11-profile-info">
          <div className="p11-label mb-4">// {tt("s1_title")}</div>
          <div className="space-y-2 text-sm" style={{ color: P11.gray2 }}>
            <div>{p.email}</div>
            <div>{tt("dob")}: {p.dob}</div>
            <div>{[p.address?.street, p.address?.house_number, p.address?.house_addition, p.address?.postal_code, p.address?.city].filter(Boolean).join(" ") || "—"}</div>
          </div>
          <div className="mt-6 space-y-6">
            <div>
              <label className="p11-label">{tt("phone")}</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className="p11-input mt-1" data-testid="p11-profile-phone" />
            </div>
            <div>
              <label className="p11-label">{tt("extra_notes")}</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 500))} rows={3} maxLength={500} className="p11-input mt-1" data-testid="p11-profile-notes" />
            </div>
            <button onClick={save} disabled={busy} className="p11-btn-ghost" data-testid="p11-profile-save">{tt("save")}</button>
          </div>
        </section>

        <section className="mb-10" data-testid="p11-profile-equipment">
          <div className="p11-label mb-4">// {tt("equipment")}</div>
          <div className="space-y-1 text-sm" style={{ color: P11.gray2 }}>
            {(tt("equipment_items")).map((item, i) => (
              <div key={i} className="flex justify-between py-1.5 border-b" style={{ borderColor: P11.border }}>
                <span>{item}</span>
                <span className="p11-label">{(p.equipment || {})[item] || "—"}</span>
              </div>
            ))}
          </div>
          <Link to="/project11/preparation" className="p11-btn-ghost mt-4" data-testid="p11-profile-equipment-edit">{tt("edit")}</Link>
        </section>

        <section className="mb-10" data-testid="p11-profile-consents">
          <div className="p11-label mb-4">// {tt("s6_title")}</div>
          <div className="space-y-2">
            {(p.consents || []).map((c) => (
              <div key={c.key} className="flex items-start gap-3 text-sm" style={{ color: P11.gray2 }}>
                <span className="p11-label mt-0.5" style={{ color: c.accepted ? P11.yellow : P11.gray }}>{c.accepted ? "✓" : "—"}</span>
                <span>{consentLabel[c.key] || c.key}</span>
              </div>
            ))}
          </div>
        </section>

        {/* privacy: inzage, correctie en verwijdering van gegevens */}
        <section className="mb-10" data-testid="p11-profile-privacy">
          <div className="p11-label mb-4">// PRIVACY</div>
          <p className="text-sm leading-relaxed" style={{ color: P11.gray2 }}>
            {lang === "nl"
              ? "Je gegevens worden alleen gebruikt voor Project 11*. Correctie of verwijdering aanvragen? Mail team@johboxing.com."
              : "Your data is only used for Project 11*. Request correction or deletion? Email team@johboxing.com."}
            {" "}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="underline underline-offset-2" style={{ color: P11.yellow }} data-testid="p11-profile-privacy-link">
              {lang === "nl" ? "Privacyverklaring" : "Privacy statement"}
            </a>
          </p>
        </section>
      </div>
    </P11Layout>
  );
}
